import { z } from "zod";
import { transformPaymentOptionsForBackend } from "@/lib/utils";

export interface ProgramTypeDefinition {
  id: string;
  name: string;
  fields: string[];
  description: string;
  requiredFields: string[];
  scheduleType: "hybrid" | "on-campus" | "via-zoom" | "online";
  defaultSchedule?: {
    hybrid?: {
      startMonth: string[];
      startDate: string[];
    };
    "on-campus"?: {
      batches: Batch[];
    };
    "via-zoom"?: {
      batches: Batch[];
    };
    online?: {
      selfPaced: boolean;
      estimatedDuration: string;
      accessPeriod: string;
    };
  };
  keyFeatureOverrides?: {
    [featureName: string]: {
      value: string;
      description: string;
    };
  };
  pricing?: {
    fullPay: number;
    biWeekly: number;
    monthly: number;
  };
}

export interface KeyFeature {
  id: number;
  name: string;
  icon: string;
  value: string;
  description: string; // Now supports rich text (HTML) with 10,000 character limit
  showPreview?: boolean;
}

export interface CourseDataItem {
  id: number;
  name: string;
  icon: string;
}

export interface Schedule {
  batch?: string;
  date?: string;
  time?: string;
}

export interface Batch {
  schedule: Schedule[];
}

export interface EnrollmentPeriod {
  id: string;
  month: string;
  year: number;
  dates: string[];
  isActive: boolean;
}

export interface OnlineSchedule {
  startMonth: string[];
  startDate: string[];
  enrollmentPeriods?: EnrollmentPeriod[];
}

export interface HybridSchedule {
  startMonth: string[];
  startDate: string[];
}

export interface BatchSchedule {
  batches: Array<{
    schedule: Array<{
      batch: string;
      date: string;
      time: string;
    }>;
  }>;
}

export interface ProgramSchedule {
  hybrid?: {
    startMonth?: string[];
    startDate?: string[];
    clinicalLocations?: string[];
    practiceHours?: number;
    capacity?: number;
    facilityRequirements?: string[];
    enrollmentPeriods?: Array<{
      id: string;
      month: string;
      year: number;
      dates: string[];
      isActive: boolean;
    }>;
  };
  "on-campus"?: {
    batches?: {
      schedule?: {
        batch?: string;
        date?: string;
        time?: string;
        capacity?: number;
      }[];
    }[];
    clinicalLocations?: string[];
    practiceHours?: number;
    capacity?: number;
    facilityRequirements?: string[];
  };
  "via-zoom"?: {
    batches?: {
      schedule?: {
        batch?: string;
        date?: string;
        time?: string;
        minParticipants?: number;
        maxParticipants?: number;
      }[];
    }[];
    clinicalLocations?: string[];
    practiceHours?: number;
    capacity?: number;
    facilityRequirements?: string[];
    minParticipants?: number;
    maxParticipants?: number;
  };
  online?: OnlineSchedule;
}

export interface FAQ {
  id: number;
  type: string;
  question: string;
  answer: string; // Rich text content (HTML)
}

export interface PodcastData {
  id: string;
  title: string;
  category: string;
  duration: string;
  imageUrl: string;
  audioUrl: string;
}

export interface SocialMedia {
  facebook?: string | null;
  twitter?: string | null;
  instagram?: string | null;
  linkedin?: string | null;
  youtube?: string | null;
  website?: string | null;
}

export interface NationalCertifications {
  isallIncluded: boolean;
  items: string[];
}

export interface PaymentOption {
  id: number;
  planType: string;
  displayName: string;
  numberOfPayments: number;
  amount: number;
  frequency: string;
  squarePlanId?: string;
  youPay: number;
  youSave: string;
  paymentTags: string;
  cardTag?: {
    available: boolean;
    value?: string;
  };
  slug?: string;
}

export const PaymentPlanSchema = z.object({
  id: z.number().positive(),
  planType: z.string(),
  displayName: z.string().min(1).max(100),
  numberOfPayments: z.number().positive().int(),
  amount: z.number().positive(),
  frequency: z.string(),
  squarePlanId: z.string().optional(),
  youPay: z.number().positive(),
  youSave: z.string(),
  paymentTags: z.string(),
  cardTag: z.object({
    available: z.boolean(),
    value: z.string().optional(),
  }),
  slug: z.string().optional(),
});

const isValidISODate = (dateString: string) => {
  return /^\d{4}-\d{2}-\d{2}$/.test(dateString);
};

export const programDataSchema = z.object({
  title: z.string().min(1).max(200),
  language: z.enum(["english", "spanish"]).default("english"),
  status: z.enum(["active", "inactive"]),
  programType: z.enum(["hybrid", "via-zoom", "on-campus", "online"]),
  defaultProgramType: z.enum(["hybrid", "via-zoom", "on-campus", "online"]),
  tagline: z.string().max(200).optional(),
  socialMedia: z
    .object({
      facebook: z.string().url().nullable().optional(),
      twitter: z.string().url().nullable().optional(),
      instagram: z.string().url().nullable().optional(),
      linkedin: z.string().url().nullable().optional(),
      youtube: z.string().url().nullable().optional(),
      website: z.string().url().nullable().optional(),
    })
    .optional(),
  categoryId: z.string(),
  availableProgramTypes: z.array(
    z.enum(["hybrid", "via-zoom", "on-campus", "online"])
  ),
  modules: z.array(
    z.object({
      label: z.string(),
      price: z.number().optional(),
      isActive: z.boolean().optional(),
      order: z.number().optional(),
    })
  ),
  keyFeatures: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      icon: z.string(),
      value: z.string(),
      description: z.string().min(10).max(10000), // Rich text support, 10-10,000 characters
    })
  ),
  typeSpecificData: z.object({
    programType: z.enum(["hybrid", "via-zoom", "on-campus", "online"]),
    delivery: z.string(),
    paymentOptions: z.array(PaymentPlanSchema),
    programSchedule: z.object({
      hybrid: z
        .object({
          startMonth: z.array(z.string()).optional(),
          startDate: z
            .array(
              z.string().refine((date) => !date || isValidISODate(date), {
                message: "Start dates must be in YYYY-MM-DD format",
              })
            )
            .optional(),
          clinicalLocations: z.array(z.string()).optional(),
          practiceHours: z.number().nonnegative().optional(),
        })
        .optional(),
      "on-campus": z
        .object({
          batches: z
            .array(
              z.object({
                schedule: z
                  .array(
                    z.object({
                      batch: z.string().optional(),
                      date: z
                        .string()
                        .refine((date) => !date || isValidISODate(date), {
                          message: "Date must be in YYYY-MM-DD format",
                        }),
                      time: z.string().optional(),
                      capacity: z.number().nonnegative().optional(),
                    })
                  )
                  .optional(),
              })
            )
            .optional(),
          facilityRequirements: z.array(z.string()).optional(),
        })
        .optional(),
      "via-zoom": z
        .object({
          batches: z
            .array(
              z.object({
                schedule: z
                  .array(
                    z.object({
                      batch: z.string().optional(),
                      date: z
                        .string()
                        .refine((date) => !date || isValidISODate(date), {
                          message: "Date must be in YYYY-MM-DD format",
                        }),
                      time: z.string().optional(),
                      minParticipants: z.number().nonnegative().optional(),
                      maxParticipants: z.number().nonnegative().optional(),
                    })
                  )
                  .optional(),
              })
            )
            .optional(),
        })
        .optional(),
      online: z
        .object({
          enrollmentPeriod: z.string().optional(),
          accessDuration: z.string().optional(),
          studyPace: z.enum(["SELF_PACED", "STRUCTURED"]).optional(),
          estimatedDuration: z.string().optional(),
          weeklyCommitment: z.string().optional(),
        })
        .optional(),
    }),
  }),
});

export const SocialMediaSchema = z.object({
  facebook: z
    .union([
      z
        .string()
        .url()
        .regex(/facebook\.com/),
      z.null(),
    ])
    .optional(),
  twitter: z
    .union([
      z
        .string()
        .url()
        .regex(/twitter\.com|x\.com/),
      z.null(),
    ])
    .optional(),
  instagram: z
    .union([
      z
        .string()
        .url()
        .regex(/instagram\.com/),
      z.null(),
    ])
    .optional(),
  linkedin: z
    .union([
      z
        .string()
        .url()
        .regex(/linkedin\.com/),
      z.null(),
    ])
    .optional(),
  youtube: z
    .union([
      z
        .string()
        .url()
        .regex(/youtube\.com/),
      z.null(),
    ])
    .optional(),
  website: z
    .union([
      z
        .string()
        .url(),
      z.null(),
    ])
    .optional(),
  tiktok: z
    .union([
      z
        .string()
        .url()
        .regex(/tiktok\.com/),
      z.null(),
    ])
    .optional(),
});

export const ProgramScheduleSchema = z.object({
  hybrid: z
    .object({
      startMonth: z.array(z.string()),
      startDate: z.array(
        z.string().refine((date) => !date || isValidISODate(date), {
          message: "Start dates must be in YYYY-MM-DD format",
        })
      ),
      clinicalLocations: z.array(z.string()),
      practiceHours: z.number().nonnegative(),
      capacity: z.number().nonnegative(),
      facilityRequirements: z.array(z.string()),
    })
    .optional(),
  "on-campus": z
    .object({
      batches: z.array(
        z.object({
          schedule: z.array(
            z.object({
              batch: z.string(),
              date: z.string().refine((date) => !date || isValidISODate(date), {
                message: "Date must be in YYYY-MM-DD format",
              }),
              time: z.string(),
              capacity: z.number().nonnegative().optional(),
            })
          ),
        })
      ),
      clinicalLocations: z.array(z.string()),
      practiceHours: z.number().nonnegative(),
      capacity: z.number().nonnegative(),
      facilityRequirements: z.array(z.string()),
    })
    .optional(),
  "via-zoom": z
    .object({
      batches: z.array(
        z.object({
          schedule: z.array(
            z.object({
              batch: z.string(),
              date: z.string().refine((date) => !date || isValidISODate(date), {
                message: "Date must be in YYYY-MM-DD format",
              }),
              time: z.string(),
              minParticipants: z.number().nonnegative().optional(),
              maxParticipants: z.number().nonnegative().optional(),
            })
          ),
        })
      ),
      clinicalLocations: z.array(z.string()),
      practiceHours: z.number().nonnegative(),
      capacity: z.number().nonnegative(),
      facilityRequirements: z.array(z.string()),
      minParticipants: z.number().nonnegative(),
      maxParticipants: z.number().nonnegative(),
    })
    .optional(),
  online: z
    .object({
      startMonth: z.array(z.string()),
      startDate: z.array(z.string()),
      enrollmentPeriods: z.array(z.object({
        id: z.string(),
        month: z.string(),
        year: z.number(),
        dates: z.array(z.string()),
        isActive: z.boolean()
      })).optional(),
    })
    .optional(),
});

export const PremiumDetailsSchema = z.object({
  premiumDescription: z.string().max(5000).optional(),
  premiumRating: z.number().min(0).max(5).optional(),
  premiumReviewCount: z.number().nonnegative().optional(),
  premiumStudentCount: z.number().nonnegative().optional(),
  premiumViewCount: z.number().nonnegative().optional(),
  premiumLikeCount: z.number().nonnegative().optional(),
  premiumShareCount: z.number().nonnegative().optional(),
});

export const programDraftSchema = z.object({
  title: z.string().min(1).max(200),
  subtitle: z.string().min(1).max(500),
  videoUrl: z
    .string()
    .url()
    .regex(/^(https?:\/\/)?(www\.)?(youtube\.com|vimeo\.com|wistia\.com)/),
  language: z.enum(["english", "spanish"]).default("english"),
  status: z.enum(["active", "inactive"]).default("active"),
  tagline: z.string().max(200).optional(),
  socialMedia: SocialMediaSchema.optional(),
  categoryId: z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid category ID"),
  programType: z.enum(["hybrid", "via-zoom", "on-campus", "online"]),
  availableProgramTypes: z
    .array(z.enum(["hybrid", "via-zoom", "on-campus", "online"]))
    .min(1),
  about: z.string().min(10).max(5000),
  modules: z
    .array(
      z.object({
        title: z.string().min(1),
        description: z.string().min(1),
        duration: z.string(),
        topics: z.array(z.string()),
      })
    )
    .min(1),
  keyFeatures: z.array(z.string()).min(1),
  thisCourseData: z.array(z.string()),
  whoisthisFor: z.array(z.string()).min(1),
  wherecaniWork: z.array(z.string()).min(1),
  programSchedule: ProgramScheduleSchema,
  paymentOptions: z.array(PaymentPlanSchema).min(1),
  clinicalProcedures: z.array(z.string()),
  nationalCertifications: z.object({
    isallIncluded: z.boolean(),
    items: z.array(z.string()),
  }),
  workPlaces: z.array(z.string()),
  faqs: z.array(
    z.object({
      question: z.string().min(1),
      answer: z.string().min(1),
    })
  ),
  podcastData: z.array(
    z.object({
      title: z.string().min(1),
      description: z.string().min(1),
      url: z.string().url(),
    })
  ),
  premiumDetails: PremiumDetailsSchema,
});

export interface DraftPayload {
  programData: Partial<ProgramForm>;
  version?: number;
}

export interface ProgramDraft {
  _id: string;
  programData: Partial<ProgramForm>;
  version: number;
  createdBy: {
    id: string;
    name: string;
  };
  lastModified: Date;
  createdAt: Date;
  updatedAt: Date;
  versionHistory?: Array<{
    version: number;
    timestamp: Date;
    changes: string[];
  }>;
  comments?: Array<{
    id: string;
    content: string;
    author: {
      id: string;
      name: string;
    };
    timestamp: Date;
    replyTo?: string;
  }>;
}

export const draftPayloadSchema = z.object({
  programData: z.any(),
  version: z.number().optional(),
});

export type ProgramType = {
  id: string;
  showLable?: boolean;
  label: string;
  paymentOptions: PaymentOption[];
};

export interface ProgramVariation {
  baseProgram: ProgramForm;
  variations: {
    [key: string]: {
      title: string;
      subtitle: string;
      keyFeatures: KeyFeature[];
      programSchedule: ProgramSchedule;
      paymentOptions: PaymentOption[];
    };
  };
}

export interface Module {
  id?: string;
  label: string;
  isActive: boolean;
  order: number;
  items: Array<{
    id: string;
    title: string;
    subtitle?: string;
    isExam: boolean;
    price: number;
  }>;
}

export interface ThisCourseData {
  id: number;
  name: string;
}

export interface WhoIsThisFor {
  id: number;
  category: string;
  title: string;
  description: string;
}
export interface WhoIsThisFor {
  id: number;
  category: string;
  title: string;
  description: string;
}

export interface WhereCanIWork {
  id: number;
  icon: string;
  title: string;
  description: string;
}

export interface ClinicalProcedure {
  id: string;
  title: string;
}

export interface ClinicalProcedureGroupItem {
  id: string;
  title: string;
}

export interface ClinicalProceduresGroup {
  label?: string;
  subHeading?: string;
  items: ClinicalProcedureGroupItem[];
}

export interface ComparisonFeature {
  id: string; // Add unique ID for better React key management
  name: string;
  included: boolean;
  includedInOther: boolean;
  otherNote?: string;
}

export interface ComparisonCategory {
  id: string; // Add unique ID for better React key management
  name: string;
  features: ComparisonFeature[];
}

export interface ProgramComparison {
  showComparisonTable: boolean;
  comparisonCategories: ComparisonCategory[];
}

// Backend API expects this format for programComparison
export interface BackendProgramComparison {
  categoryName: string;
  features: Array<{
    id: string | number;
    name: string;
    description: string;
    icon: string;
    value: string;
    included: boolean;
    includedInOther: boolean;
  }>;
}

// Helper function to generate IDs for array items
const generateIdsForArrayItems = (data: any): any => {
  if (Array.isArray(data)) {
    return data.map((item, index) => {
      if (typeof item === 'object' && item !== null) {
        const newItem = { ...item };
        
        // Generate ID based on the item type and index
        if (!newItem.id) {
          if (typeof item.id === 'number') {
            newItem.id = index + 1; // For numeric IDs
          } else {
            newItem.id = `item-${index + 1}`; // For string IDs
          }
        }
        
        // Recursively process nested arrays
        Object.keys(newItem).forEach(key => {
          if (Array.isArray(newItem[key])) {
            newItem[key] = generateIdsForArrayItems(newItem[key]);
          }
        });
        
        return newItem;
      }
      return item;
    });
  }
  return data;
};

// Helper function to generate specific IDs for different field types
const generateSpecificIds = (data: any, fieldType: string): any => {
  if (Array.isArray(data)) {
    return data.map((item, index) => {
      if (typeof item === 'object' && item !== null) {
        const newItem = { ...item };
        
        // Generate appropriate ID based on field type
        if (!newItem.id) {
          switch (fieldType) {
            case 'modules':
              // Module items need string IDs
              newItem.id = `module-${index + 1}`;
              break;
            case 'keyFeatures':
              // Key features need numeric IDs
              newItem.id = index + 1;
              break;
            case 'thisCourseData':
              // Course data needs numeric IDs
              newItem.id = index + 1;
              break;
            case 'whoisthisFor':
              // Who is this for needs numeric IDs
              newItem.id = index + 1;
              break;
            case 'wherecaniWork':
              // Where can I work needs numeric IDs
              newItem.id = index + 1;
              break;
            case 'clinicalProcedures':
              // Clinical procedures need string IDs
              newItem.id = `procedure-${index + 1}`;
              break;
            case 'faqs':
              // FAQs need numeric IDs
              newItem.id = index + 1;
              break;
            case 'podcastData':
              // Podcast data needs string IDs
              newItem.id = `podcast-${index + 1}`;
              break;
            default:
              newItem.id = `item-${index + 1}`;
          }
        }
        
        // Handle nested arrays (like module items)
        if (fieldType === 'modules' && newItem.items) {
          newItem.items = newItem.items.map((subItem: any, subIndex: number) => ({
            ...subItem,
            id: subItem.id || `module-item-${index + 1}-${subIndex + 1}`
          }));
        }
        
        return newItem;
      }
      return item;
    });
  }
  return data;
};

// Helper function to transform frontend ProgramComparison to backend format
export const transformProgramComparisonToBackend = (
  keyFeatures: KeyFeature[]
): BackendProgramComparison => {
  return {
    categoryName: "Program Features", // Default category name
    features: keyFeatures && keyFeatures.length > 0
      ? keyFeatures.map((feature, index) => ({
          id: feature.id || `feature_${index}`,
          name: feature.name || `Feature ${index + 1}`,
          description: feature.description || '',
          icon: feature.icon || 'star',
          value: feature.value || '',
          included: true, // Default to included
          includedInOther: false // Default to not included in other programs
        }))
      : [
          {
            id: 'default_feature',
            name: 'Quality Education',
            description: 'Comprehensive learning experience',
            icon: 'star',
            value: 'Included',
            included: true,
            includedInOther: false
          }
        ]
  };
};

// Helper function to transform form's ProgramComparison to backend format
export const transformFormProgramComparisonToBackend = (
  programComparison: ProgramComparison
): BackendProgramComparison => {
  // If no comparison table is shown, return empty structure
  if (!programComparison.showComparisonTable || !programComparison.comparisonCategories.length) {
    return {
      categoryName: "Program Features",
      features: []
    };
  }

  // Get the first category (assuming single category for now)
  const firstCategory = programComparison.comparisonCategories[0];
  
  return {
    categoryName: firstCategory.name || "Program Features",
    features: firstCategory.features.map((feature, index) => ({
      id: feature.id || `feature_${index}`,
      name: feature.name || `Feature ${index + 1}`,
      description: '', // Form features don't have descriptions
      icon: 'star', // Default icon
      value: feature.name, // Use name as value for form features
      included: feature.included,
      includedInOther: feature.includedInOther
    }))
  };
};

export type DeliveryMode = "hybrid" | "via-zoom" | "on-campus" | "online";
export type Language = "english" | "spanish";
export type Status = "active" | "inactive" | "draft";

export interface BatchSchedule {
  schedule?: Schedule[];
}

export interface HybridScheduleOld {
  startMonth?: string[];
  startDate?: string[];
}

export interface TypeSpecificData {
  programType?: DeliveryMode;
  delivery?: string;
  subtitle?: string;
  about?: string;
  videoUrl?: string;
  nationalCertifications?: NationalCertifications;
  podcastData?: PodcastData[];
  paymentOptions?: PaymentOption[];
  keyFeatures?: KeyFeature[];
  // Internal flag to track if this program type has been configured
  isConfigured?: boolean;

  programSchedule?: ProgramSchedule;
  marketing?: {
    socialLinks?: Array<{
      platform: string;
      url: string;
      isActive: boolean;
    }>;
    faqs?: Array<{
    question: string;
    answer: string;
      category: string;
  }>;
    promotionalContent?: Array<{
    title: string;
    description: string;
      type: "video" | "image" | "document";
      url?: string;
      file?: File;
    }>;
    marketingMaterials?: File[];
  };
}

export interface BreadcrumbItem {
  label: string;
  href: string;
}

export interface APICompatibleProgram {
  title: string;
  language: Language;
  status: Status;
  tagline?: string;
  categoryId: string; // Always string in API format
  socialMedia?: SocialMedia;
  modules: Module[];
  keyFeatures: KeyFeature[];
  breadcrumb: BreadcrumbItem[];
  thisCourseData?: ThisCourseData[];
  whoisthisFor?: WhoIsThisFor[];
  wherecaniWork?: WhereCanIWork[];
  clinicalProcedures?: ClinicalProcedure[];
  clinicalProceduresGrouped?: ClinicalProceduresGroup[];
  workPlaces?: string[];
  faqs?: FAQ[];
  programComparison?: ProgramComparison;
  premiumDescription?: string;
  premiumTagline?: string;
  marketingTags?: string[];
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
  premiumShareCount?: number;
  defaultProgramType: string;
  programTypeDataMap: {
    [key in DeliveryMode]?: APICompatibleProgramTypeData;
  };
  typeSpecificData: APICompatibleProgramTypeData;
  // Enhanced module configuration - match API specification exactly
  payAsYouGo?: boolean;
  modulePricingEnabled?: boolean;
  moduleConfiguration?: ModuleConfiguration;
  modulePricingMetadata?: {
    totalModules: number;
    totalPrice: number;
    averageModulePrice: number;
    bundlePrice?: number;
    savingsWithBundle?: number;
  };
  // Duration field
  duration?: {
    value: number;
    isInWeeks: boolean;
  };
  // Platform configuration
  platform?: {
    isIspring?: boolean;
    contentTitle?: string;
    contentItemId?: string;
  };
  // For Those Searching field
  forThoseSearching?: string[];
}

export interface APICompatibleProgramTypeData
  extends Omit<
    APICompatibleProgram,
    "programTypeDataMap" | "typeSpecificData" | "defaultProgramType"
  > {
  programType: DeliveryMode;
  delivery: string;
  subtitle: string;
  about: string;
  videoUrl?: string;
  nationalCertifications?: NationalCertifications;
  podcastData?: PodcastData[];
  schedule: HybridSchedule | BatchSchedule;
  paymentOptions: PaymentOption[];
}

export interface ProgramForm {
  title: string;
  language: Language;
  status: Status;
  programType: DeliveryMode;
  defaultProgramType: DeliveryMode;
  tagline?: string;
  socialMedia?: SocialMedia;
  categoryId: string | { _id: string; title?: string; description?: string };
  platform?: {
    isIspring?: boolean;
    contentTitle?: string;
    contentItemId?: string;
  };
  availableProgramTypes: DeliveryMode[];
  modules: Module[];
  keyFeatures: KeyFeature[];
  thisCourseData?: ThisCourseData[];
  whoisthisFor?: WhoIsThisFor[];
  wherecaniWork?: WhereCanIWork[];
  clinicalProcedures?: ClinicalProcedure[];
  clinicalProceduresGrouped?: ClinicalProceduresGroup[];
  workPlaces?: string[];
  faqs?: FAQ[];
  breadcrumb?: BreadcrumbItem[];
  programTypes?: ProgramType[];
  programComparison?: ProgramComparison;
  typeSpecificData: TypeSpecificData;
  // Optional per-type storage for editing multiple types without losing data when switching
  typeSpecificDataByType?: Partial<Record<DeliveryMode, TypeSpecificData>>;
  // Custom fields for type-specific data to avoid complex type issues
  customFields?: Record<string, any>;
  // Internal flag to track if this program type has been configured
  isConfigured?: boolean;
  premiumDescription?: string;
  premiumTagline?: string;
  marketingTags?: string[];
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
  premiumShareCount?: number;
  // Enhanced module configuration
  payAsYouGo?: boolean;
  moduleConfiguration?: ModuleConfiguration;
  modulePricingMetadata?: ModulePricingMetadata;
  // Duration field
  duration?: {
    value: number;
    isInWeeks: boolean;
  };
  // For Those Searching field
  forThoseSearching?: string[];
}

export const DELIVERY_MODE_MAP: Record<DeliveryMode, string> = {
  hybrid: "Hybrid",
  "via-zoom": "Via-Zoom",
  "on-campus": "On-Campus",
  online: "Online",
};

export const PROGRAM_TYPE_LABELS: Record<DeliveryMode, string> = {
  hybrid: "Hybrid",
  "via-zoom": "Via-Zoom",
  "on-campus": "On-Campus",
  online: "Online",
};

// Data validation function
export const validateProgramFormData = (
  formData: ProgramForm
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required field validation - DISABLED FOR NOW
  
  // if (!formData.title?.trim()) errors.push("Program title is required");
  // if (!formData.subtitle?.trim()) errors.push("Program subtitle is required");
  // if (!formData.videoUrl?.trim()) errors.push("Video URL is required");
  // if (!formData.categoryId) errors.push("Category is required");
  // if (!formData.about?.trim()) errors.push("Program description is required");
  // if (!formData.programType) errors.push("Program type is required");
  // if (!formData.defaultProgramType)
  //   errors.push("Default program type is required");
  // 
  // // Duration validation
  // if (!formData.duration) {
  //   errors.push("Program duration is required");
  // } else {
  //   if (!formData.duration.value || formData.duration.value < 1) {
  //     errors.push("Duration value must be at least 1");
  //   }
  //   if (formData.duration.isInWeeks === undefined || formData.duration.isInWeeks === null) {
  //     errors.push("Duration unit is required");
  //   }
  // }

  // Array validation
  if (!formData.modules || formData.modules.length === 0) {
    errors.push("At least one module is required");
  }
  if (!formData.keyFeatures || formData.keyFeatures.length === 0) {
    errors.push("At least one key feature is required");
  }

  // Type-specific data validation
  if (!formData.typeSpecificData) {
    errors.push("Program type specific data is required");
  } else {
    if (
      !formData.typeSpecificData.paymentOptions ||
      formData.typeSpecificData.paymentOptions.length === 0
    ) {
      errors.push("At least one payment option is required");
    }
    if (!formData.typeSpecificData.programSchedule) {
      errors.push("Program schedule is required");
    }
  }

  return { isValid: errors.length === 0, errors };
};

// Interface for the actual backend API call (with BackendProgramComparison)
export interface BackendAPIProgram extends Omit<APICompatibleProgram, 'programComparison'> {
  programComparison: BackendProgramComparison;
}

// Simplified helper functions for data transformation
const getCategoryId = (categoryId: string | { _id: string; title?: string; description?: string }): string => {
  if (typeof categoryId === "object" && categoryId?._id) {
    return categoryId._id;
  }
  if (typeof categoryId === "string") {
    return categoryId;
  }
  throw new Error("Category ID is required and must be a valid string");
};

// Removed unused function getScheduleForType

// Removed unused function createTypeSpecificData

const createProgramTypeDataMap = (formData: ProgramForm): Record<DeliveryMode, APICompatibleProgramTypeData> => {
  const configuredTypes = formData.availableProgramTypes || [formData.programType];
  
  // Create shared data structure (same across all program types)
  const sharedData = {
    title: formData.title,
    language: formData.language,
    status: formData.status,
    tagline: formData.tagline,
    socialMedia: formData.socialMedia,
    breadcrumb: formData.breadcrumb || [],
    duration: formData.duration,
  };

  // getTypeSpecificData helper removed (unused in this scope)
  
  return configuredTypes.reduce((acc, type) => {
    const typeData = formData.typeSpecificDataByType?.[type] || formData.typeSpecificData;
    const programSchedule = typeData?.programSchedule || {};
    const paymentOptions = typeData?.paymentOptions || [];
    const transformedPaymentOptions = transformPaymentOptionsForBackend(paymentOptions);
    
    // Create properly nested schedule structure for backend compatibility
    let schedule: any = {};
    if (type === 'hybrid') {
      // Hybrid uses direct schedule fields - nest under program type
      const hybridSchedule = (programSchedule as any).hybrid || programSchedule;
      schedule = {
        hybrid: {
          startMonth: hybridSchedule.startMonth || [],
          startDate: hybridSchedule.startDate || [],
          clinicalLocations: hybridSchedule.clinicalLocations || [],
          practiceHours: hybridSchedule.practiceHours || 0,
          enrollmentPeriods: hybridSchedule.enrollmentPeriods || []
        }
      };
    } else if (type === 'via-zoom') {
      // Via-zoom uses batches array - nest under program type
      const viaZoomSchedule = (programSchedule as any)['via-zoom'] || programSchedule;
      schedule = {
        viaZoom: {
          batches: viaZoomSchedule.batches || [],
          clinicalLocations: viaZoomSchedule.clinicalLocations || [],
          practiceHours: viaZoomSchedule.practiceHours || 0,
          minParticipants: viaZoomSchedule.minParticipants || 0,
          maxParticipants: viaZoomSchedule.maxParticipants || 0
        }
      };
    } else if (type === 'on-campus') {
      // On-campus uses batches array with facility requirements - nest under program type
      const onCampusSchedule = (programSchedule as any)['on-campus'] || programSchedule;
      schedule = {
        onCampus: {
          batches: onCampusSchedule.batches || [],
          facilityRequirements: onCampusSchedule.facilityRequirements || [],
          clinicalLocations: onCampusSchedule.clinicalLocations || [],
          practiceHours: onCampusSchedule.practiceHours || 0,
          capacity: onCampusSchedule.capacity || 0
        }
      };
    } else if (type === 'online') {
      // Online uses hybrid-like schedule structure - nest under program type
      const onlineSchedule = (programSchedule as any).online || programSchedule;
      schedule = {
        online: {
          startMonth: onlineSchedule.startMonth || [],
          startDate: onlineSchedule.startDate || [],
          enrollmentPeriods: onlineSchedule.enrollmentPeriods || []
        }
      };
    }
    
    // Get type-specific data for this program type (with IDs generated)
    const td: any = typeData as any;
    const typeSpecificData = {
      subtitle: td?.subtitle || "",
      about: td?.about || "",
      videoUrl: td?.videoUrl,
      modules: generateSpecificIds(td?.modules || [], 'modules'),
      keyFeatures: generateSpecificIds(td?.keyFeatures || [], 'keyFeatures'),
      thisCourseData: generateSpecificIds(td?.thisCourseData || [], 'thisCourseData'),
      whoisthisFor: generateSpecificIds(td?.whoisthisFor || [], 'whoisthisFor'),
      wherecaniWork: generateSpecificIds(td?.wherecaniWork || [], 'wherecaniWork'),
      clinicalProcedures: generateSpecificIds(td?.clinicalProcedures || [], 'clinicalProcedures'),
      clinicalProceduresGrouped: td?.clinicalProceduresGrouped || [],
      nationalCertifications: td?.nationalCertifications || { isallIncluded: false, items: [] },
      workPlaces: td?.workPlaces || [],
      faqs: generateSpecificIds(td?.faqs || [], 'faqs'),
      podcastData: generateSpecificIds(td?.podcastData || [], 'podcastData'),
      programComparison: td?.programComparison || { showComparisonTable: false, comparisonCategories: [] },
      premiumDescription: td?.premiumDescription,
      premiumTagline: td?.premiumTagline,
      premiumRating: td?.premiumRating,
      premiumReviewCount: td?.premiumReviewCount,
      premiumStudentCount: td?.premiumStudentCount,
      premiumViewCount: td?.premiumViewCount,
      premiumLikeCount: td?.premiumLikeCount,
      premiumShareCount: td?.premiumShareCount,
    };
    
    // Update Delivery value in keyFeatures (after ID generation)
    const updatedKeyFeatures = typeSpecificData.keyFeatures.map((feature: any) => {
      if (feature.name === "Delivery") {
        return {
          ...feature,
          value: PROGRAM_TYPE_LABELS[type] || 'Hybrid'
        };
      }
      return feature;
    });
    
    acc[type] = {
      ...sharedData, // Use shared data (same across all types)
      ...typeSpecificData, // Use type-specific data (different per type)
      programType: type,
      delivery: PROGRAM_TYPE_LABELS[type],
      schedule: schedule,
      paymentOptions: transformedPaymentOptions,
      categoryId: getCategoryId(formData.categoryId),
      keyFeatures: updatedKeyFeatures // Use type-specific keyFeatures with correct Delivery value and IDs
    };
    return acc;
  }, {} as Record<DeliveryMode, APICompatibleProgramTypeData>);
};

// Simplified main transformation function
export const transformToAPIFormat = (formData: ProgramForm): CreateProgramV2Request => {
  
  // const validation = validateProgramFormData(formData);
  // if (!validation.isValid) {
  //   throw new Error(`Validation failed: ${validation.errors.join(", ")}`);
  // }

  // Validate required fields - DISABLED FOR NOW
  // if (!formData.typeSpecificData?.paymentOptions || !Array.isArray(formData.typeSpecificData.paymentOptions)) {
  //   throw new Error("Payment options are required and must be an array");
  // }
  // if (!formData.typeSpecificData?.programSchedule) {
  //   throw new Error("Program schedule is required");
  // }
  // if (!formData.programType || !PROGRAM_TYPE_LABELS[formData.programType]) {
  //   throw new Error(`Invalid program type: ${formData.programType}`);
  // }

  // Create shared data structure (same across all program types)
  const sharedData = {
    title: formData.title,
    language: formData.language,
    status: formData.status,
    tagline: formData.tagline,
    socialMedia: formData.socialMedia,
    breadcrumb: formData.breadcrumb || [],
    categoryId: getCategoryId(formData.categoryId),
    duration: formData.duration,
    platform: formData.platform,
    forThoseSearching: formData.forThoseSearching,
  };

  // helper removed

  const baseData = {
    ...sharedData,
    // Use Step 1 data directly for the main program object - preserve original structure
    modules: formData.modules || [],
    keyFeatures: formData.keyFeatures || [],
    thisCourseData: formData.thisCourseData || [],
    whoisthisFor: formData.whoisthisFor || [],
    wherecaniWork: formData.wherecaniWork || [],
    clinicalProcedures: formData.clinicalProcedures || [],
    clinicalProceduresGrouped: formData.clinicalProceduresGrouped || [],
    workPlaces: formData.workPlaces || [],
    faqs: formData.faqs || [],
    programComparison: formData.programComparison || { showComparisonTable: false, comparisonCategories: [] },
    premiumDescription: formData.premiumDescription,
    premiumTagline: formData.premiumTagline,
    marketingTags: formData.marketingTags ?? [],
    premiumRating: formData.premiumRating,
    premiumReviewCount: formData.premiumReviewCount,
    premiumStudentCount: formData.premiumStudentCount,
    premiumViewCount: formData.premiumViewCount,
    premiumLikeCount: formData.premiumLikeCount,
    premiumShareCount: formData.premiumShareCount,
  };

  // Create program type data map
  const programTypeDataMap = createProgramTypeDataMap(formData);

  // Return the correct API structure: { program: {...}, programTypes: [...] }
  return {
    program: {
      ...baseData,
      duration: baseData.duration || { value: 1, isInWeeks: true }, // Ensure duration is always defined
      defaultProgramType: formData.defaultProgramType || formData.programType,
    } as ProgramV2,
    programTypes: Object.values(programTypeDataMap).map(typeData => ({
      type: typeData.programType,
      delivery: typeData.delivery as "Hybrid" | "Via-Zoom" | "On-Campus" | "Online",
      subtitle: typeData.subtitle || "",
      about: typeData.about || "",
      videoUrl: typeData.videoUrl,
      nationalCertifications: typeData.nationalCertifications,
      podcastData: typeData.podcastData || [],
      schedule: typeData.schedule || {},
      keyFeatures: typeData.keyFeatures || [],
      paymentOptions: typeData.paymentOptions || [],
      workPlaces: typeData.workPlaces || [],
      faqs: typeData.faqs || [],
    } as ProgramTypeV2Request))
  };
};

export interface ModuleConfiguration {
  payAsYouGo: boolean;
  modulePricingEnabled?: boolean; // Made optional since backend no longer requires it
  modules: EnhancedModuleItem[];
  pricingStrategy?: "INDIVIDUAL" | "BUNDLE";
  bundleDiscount?: number;
  minimumModulesForBundle?: number;
  moduleAccessType?: "SEQUENTIAL" | "NON_SEQUENTIAL";
}

export interface EnhancedModuleItem {
  moduleId: string;
  _id?: string;
  name: string;
  description?: string;
  programId?: string;
  price: number;
  isActive: boolean;
  order: number;
  metadata?: {
    duration?: number;
    difficulty?: "BEGINNER" | "INTERMEDIATE" | "ADVANCED";
    tags?: string[];
    videoUrl?: string;
    resources?: Array<{
      type: "PDF" | "VIDEO" | "AUDIO" | "LINK";
      title: string;
      url: string;
    }>;
  };
  prerequisites?: string[];
  estimatedCompletionTime?: number;
  isExam?: boolean;
  examDetails?: {
    passingScore: number;
    timeLimit?: number;
    questions?: number;
  };
}

export interface ModulePricingMetadata {
  totalModules: number;
  totalPrice: number;
  averageModulePrice: number;
  bundlePrice?: number;
  savingsWithBundle?: number;
}

// ============================================================================
// PROGRAMS V2 API TYPES
// ============================================================================

// Program V2 Schema (Common fields shared across all program types)
export interface ProgramV2 {
  programId?: string; // Auto-generated, optional in request
  title: string;
  slug?: string; // Optional, sparse index
  categoryId: string; // ObjectId reference to categories
  
  duration: {
    value: number; // min: 1
    isInWeeks: boolean;
  };
  
  language: "english" | "spanish";
  status: "active" | "inactive"; // default: "active"
  
  availableProgramTypes?: string[]; // ObjectId references to programTypes
  defaultProgramType: string; // ObjectId reference to programTypes, required
  
  tagline?: string;
  socialMedia?: {
    facebook?: string | null;
    twitter?: string | null;
    instagram?: string | null;
    linkedin?: string | null;
    youtube?: string | null;
    website?: string | null;
  };
  
  modules?: Array<{
    label: string; // required
    order?: number; // default: 0
    isActive?: boolean; // default: true
    items?: Array<{
      id?: string;
      title?: string;
      subtitle?: string;
      isExam?: boolean; // default: false
      price?: number; // default: 0
    }>;
  }>;
  
  platform?: {
    isIspring?: boolean; // default: false
    contentTitle?: string;
    contentItemId?: string;
  };
  
  // New common fields
  isPublished?: boolean; // default: false
  
  // Content arrays
  thisCourseIncludesData?: Array<{
    id: number;
    name: string;
  }>;
  
  whoisthisFor?: Array<{
    id: number;
    category: string;
    title: string;
    description: string; // Rich text
  }>;
  
  wherecaniWork?: Array<{
    id: number;
    title: string;
    description: string; // Rich text
  }>;
  
  clinicalProceduresGrouped?: Array<{
    label: string;
    subHeading?: string;
    items: Array<{
      id: string;
      title: string;
    }>;
  }>;
  
  programComparison?: any; // Mixed type
  premiumShareCount?: number; // default: 0
  
  // Premium fields
  premiumDescription?: string;
  premiumTagline?: string;
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
  
  createdBy?: string; // ObjectId reference to admins
  updatedBy?: string; // ObjectId reference to admins
  
  // MongoDB timestamps
  createdAt?: Date;
  updatedAt?: Date;
}

// ProgramType V2 Schema (Type-specific fields)
export interface ProgramTypeV2 {
  programId: string; // ObjectId reference to programs, required
  type: "hybrid" | "via-zoom" | "on-campus" | "online";
  delivery: "Hybrid" | "Via-Zoom" | "On-Campus" | "Online";
  
  subtitle: string;
  about: string;
  videoUrl?: string;
  nationalCertifications?: any;
  podcastData?: Array<{
    id: string;
    title: string;
    category: string;
    duration: string;
    imageUrl: string;
    audioUrl: string;
  }>;
  
  schedule?: {
    batches?: Array<{
      batch: string;
      date: string;
      time: string;
      minParticipants?: number;
      maxParticipants?: number;
      capacity?: number;
      clinicalLocations?: string[];
    }>;
    enrollmentPeriods?: Array<{
      id: string;
      month: string;
      year: number;
      dates: string[];
      isActive?: boolean; // default: true
    }>;
    practiceHours?: number; // default: 0
    facilityRequirements?: string[];
  };
  
  keyFeatures?: Array<{
    id: number;
    name: string;
    icon: string;
    value: string;
    description: string; // Rich text
  }>;
  
  paymentOptions?: Array<{
    id: number;
    planType: string;
    displayName: string;
    paymentOptionName?: string;
    numberOfPayments: number;
    amount: number;
    frequency: string;
    squarePlanId?: string;
    youPay: number;
    youSave: string;
    paymentTags: string;
    cardTag?: {
      available: boolean;
      value?: string;
    };
    slug?: string;
    currency?: string; // default: 'USD'
  }>;
  
  workPlaces?: string[];
  faqs?: Array<{
    id: number;
    type: string;
    question: string;
    answer: string; // Rich text
  }>;
  
  // MongoDB timestamps
  createdAt?: Date;
  updatedAt?: Date;
}

// ProgramType V2 for API requests (without programId)
export interface ProgramTypeV2Request {
  type: "hybrid" | "via-zoom" | "on-campus" | "online";
  delivery: "Hybrid" | "Via-Zoom" | "On-Campus" | "Online";
  
  subtitle: string;
  about: string;
  videoUrl?: string;
  nationalCertifications?: any;
  podcastData?: Array<{
    id: string;
    title: string;
    category: string;
    duration: string;
    imageUrl: string;
    audioUrl: string;
  }>;
  
  schedule?: {
    batches?: Array<{
      batch: string;
      date: string;
      time: string;
      minParticipants?: number;
      maxParticipants?: number;
      capacity?: number;
      clinicalLocations?: string[];
    }>;
    enrollmentPeriods?: Array<{
      id: string;
      month: string;
      year: number;
      dates: string[];
      isActive?: boolean; // default: true
    }>;
    practiceHours?: number; // default: 0
    facilityRequirements?: string[];
  };
  
  keyFeatures?: Array<{
    id: number;
    name: string;
    icon: string;
    value: string;
    description: string; // Rich text
  }>;
  
  paymentOptions?: Array<{
    id: number;
    planType: string;
    displayName: string;
    paymentOptionName?: string;
    numberOfPayments: number;
    amount: number;
    frequency: string;
    squarePlanId?: string;
    youPay: number;
    youSave: string;
    paymentTags: string;
    cardTag?: {
      available: boolean;
      value?: string;
    };
    slug?: string;
    currency?: string; // default: 'USD'
  }>;
  
  workPlaces?: string[];
  faqs?: Array<{
    id: number;
    type: string;
    question: string;
    answer: string; // Rich text
  }>;
}

// API Request/Response Types
export interface CreateProgramV2Request {
  program: ProgramV2;
  programTypes: ProgramTypeV2Request[];
}

export interface CreateProgramV2Response {
  success: boolean;
  message: string;
  programId?: string;
  customProgramId?: string;
  programTypes?: string[];
  errors?: any[];
}

export interface UpdateProgramV2Request {
  program?: Partial<ProgramV2>;
  programTypes?: Array<Partial<ProgramTypeV2> & { _id?: string }>;
}

export interface UpdateProgramV2Response {
  success: boolean;
  message: string;
  programId?: string;
  customProgramId?: string;
  programTypes?: string[];
  errors?: any[];
}

// Frontend Form Types for V2
export interface ProgramV2Form {
  // Step 1 - Common fields
  title: string;
  subtitle: string;
  categoryId: string;
  about: string;
  duration: {
    value: number;
    isInWeeks: boolean;
  };
  language: "english" | "spanish";
  status: "active" | "inactive";
  videoUrl?: string;
  isPublished?: boolean;
  socialMedia?: {
    facebook?: string | null;
    twitter?: string | null;
    instagram?: string | null;
    linkedin?: string | null;
    youtube?: string | null;
    website?: string | null;
  };
  thisCourseIncludesData?: Array<{
    id: number;
    name: string;
  }>;
  whoisthisFor?: Array<{
    id: number;
    category: string;
    title: string;
    description: string;
  }>;
  wherecaniWork?: Array<{
    id: number;
    title: string;
    description: string;
  }>;
  clinicalProceduresGrouped?: Array<{
    label: string;
    subHeading?: string;
    items: Array<{
      id: string;
      title: string;
    }>;
  }>;
  modules?: Array<{
    label: string;
    order?: number;
    isActive?: boolean;
    items?: Array<{
      id?: string;
      title?: string;
      subtitle?: string;
      isExam?: boolean;
      price?: number;
    }>;
  }>;
  nationalCertifications?: any;
  podcastData?: Array<{
    id: string;
    title: string;
    category: string;
    duration: string;
    imageUrl: string;
    audioUrl: string;
  }>;
  programComparison?: any;
  premiumShareCount?: number;
  premiumDescription?: string;
  premiumTagline?: string;
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount?: number;
  premiumViewCount?: number;
  premiumLikeCount?: number;
  
  // Step 2 - Program type specific data
  programTypes: Array<{
    type: "hybrid" | "via-zoom" | "on-campus" | "online";
    delivery: "Hybrid" | "Via-Zoom" | "On-Campus" | "Online";
    schedule?: {
      batches?: Array<{
        batch: string;
        date: string;
        time: string;
        minParticipants?: number;
        maxParticipants?: number;
        capacity?: number;
        clinicalLocations?: string[];
      }>;
      enrollmentPeriods?: Array<{
        id: string;
        month: string;
        year: number;
        dates: string[];
        isActive?: boolean;
      }>;
      practiceHours?: number;
      facilityRequirements?: string[];
    };
    keyFeatures?: Array<{
      id: number;
      name: string;
      icon: string;
      value: string;
      description: string;
    }>;
    paymentOptions?: Array<{
      id: number;
      planType: string;
      displayName: string;
      paymentOptionName?: string;
      numberOfPayments: number;
      amount: number;
      frequency: string;
      squarePlanId?: string;
      youPay: number;
      youSave: string;
      paymentTags: string;
      cardTag?: {
        available: boolean;
        value?: string;
      };
      slug?: string;
      currency?: string;
    }>;
    workPlaces?: string[];
    faqs?: Array<{
      id: number;
      type: string;
      question: string;
      answer: string;
    }>;
  }>;
}

// Validation schemas for V2
export const ProgramV2Schema = z.object({
  programId: z.string().optional(),
  title: z.string().min(1).max(200),
  slug: z.string().optional(),
  categoryId: z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid category ID"),
  
  duration: z.object({
    value: z.number().min(1),
    isInWeeks: z.boolean()
  }),
  
  language: z.enum(["english", "spanish"]),
  status: z.enum(["active", "inactive"]).default("active"),
  
  availableProgramTypes: z.array(z.string().regex(/^[0-9a-fA-F]{24}$/)).optional(),
  defaultProgramType: z.string().regex(/^[0-9a-fA-F]{24}$/),
  
  tagline: z.string().optional(),
  socialMedia: z.object({
    facebook: z.string().nullable().optional(),
    twitter: z.string().nullable().optional(),
    instagram: z.string().nullable().optional(),
    linkedin: z.string().nullable().optional(),
    youtube: z.string().nullable().optional(),
    website: z.string().nullable().optional()
  }).optional(),
  
  modules: z.array(z.object({
    label: z.string(),
    order: z.number().default(0),
    isActive: z.boolean().default(true),
    items: z.array(z.object({
      id: z.string().optional(),
      title: z.string().optional(),
      subtitle: z.string().optional(),
      isExam: z.boolean().default(false),
      price: z.number().min(0).max(10000).default(0)
    })).optional()
  })).optional(),
  
  platform: z.object({
    isIspring: z.boolean().default(false),
    contentTitle: z.string().optional(),
    contentItemId: z.string().optional()
  }).optional(),
  
  isPublished: z.boolean().default(false),
  
  thisCourseIncludesData: z.array(z.object({
    id: z.number(),
    name: z.string()
  })).optional(),
  
  whoisthisFor: z.array(z.object({
    id: z.number(),
    category: z.string(),
    title: z.string(),
    description: z.string().min(10).max(10000) // Rich text
  })).optional(),
  
  wherecaniWork: z.array(z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().min(10).max(10000) // Rich text
  })).optional(),
  
  clinicalProceduresGrouped: z.array(z.object({
    label: z.string(),
    subHeading: z.string().optional(),
    items: z.array(z.object({
      id: z.string(),
      title: z.string()
    }))
  })).optional(),
  
  programComparison: z.any().optional(),
  premiumShareCount: z.number().default(0),

  marketingTags: z.array(z.string().trim().min(1).max(40)).max(8).optional(),
  
  premiumDescription: z.string().optional(),
  premiumTagline: z.string().optional(),
  premiumRating: z.number().optional(),
  premiumReviewCount: z.number().optional(),
  premiumStudentCount: z.number().optional(),
  premiumViewCount: z.number().optional(),
  premiumLikeCount: z.number().optional(),
  
  createdBy: z.string().optional(),
  updatedBy: z.string().optional()
});

export const ProgramTypeV2Schema = z.object({
  programId: z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid program ID"),
  type: z.enum(["hybrid", "via-zoom", "on-campus", "online"]),
  delivery: z.enum(["Hybrid", "Via-Zoom", "On-Campus", "Online"]),
  
  subtitle: z.string().min(1).max(500),
  about: z.string().min(10).max(10000),
  videoUrl: z.string().optional(),
  nationalCertifications: z.any().optional(),
  podcastData: z.array(z.object({
    id: z.string(),
    title: z.string(),
    category: z.string(),
    duration: z.string(),
    imageUrl: z.string(),
    audioUrl: z.string()
  })).optional(),
  
  schedule: z.object({
    batches: z.array(z.object({
      batch: z.string(),
      date: z.string(),
      time: z.string(),
      minParticipants: z.number().optional(),
      maxParticipants: z.number().optional(),
      capacity: z.number().optional(),
      clinicalLocations: z.array(z.string()).optional()
    })).optional(),
    enrollmentPeriods: z.array(z.object({
      id: z.string(),
      month: z.string(),
      year: z.number(),
      dates: z.array(z.string()),
      isActive: z.boolean().default(true)
    })).optional(),
    practiceHours: z.number().default(0),
    facilityRequirements: z.array(z.string()).optional()
  }).optional(),
  
  keyFeatures: z.array(z.object({
    id: z.number(),
    name: z.string(),
    icon: z.string(),
    value: z.string(),
    description: z.string().min(10).max(10000) // Rich text
  })).optional(),
  
  paymentOptions: z.array(z.object({
    id: z.number(),
    planType: z.string(),
    displayName: z.string(),
    paymentOptionName: z.string().optional(),
    numberOfPayments: z.number(),
    amount: z.number(),
    frequency: z.string(),
    squarePlanId: z.string().optional(),
    youPay: z.number(),
    youSave: z.string(),
    paymentTags: z.string(),
    cardTag: z.object({
      available: z.boolean(),
      value: z.string().optional()
    }).optional(),
    slug: z.string().optional(),
    currency: z.string().default('USD')
  })).optional(),
  
  workPlaces: z.array(z.string()).optional(),
  faqs: z.array(z.object({
    id: z.number(),
    type: z.string(),
    question: z.string(),
    answer: z.string().min(10).max(10000) // Rich text
  })).optional()
});

export const CreateProgramV2RequestSchema = z.object({
  program: ProgramV2Schema,
  programTypes: z.array(ProgramTypeV2Schema)
});

export const UpdateProgramV2RequestSchema = z.object({
  program: ProgramV2Schema.partial().optional(),
  programTypes: z.array(ProgramTypeV2Schema.partial().extend({
    _id: z.string().optional()
  })).optional()
});

// Type exports
export type ProgramV2Type = z.infer<typeof ProgramV2Schema>;
export type ProgramTypeV2Type = z.infer<typeof ProgramTypeV2Schema>;
export type CreateProgramV2RequestType = z.infer<typeof CreateProgramV2RequestSchema>;
export type UpdateProgramV2RequestType = z.infer<typeof UpdateProgramV2RequestSchema>;


